<script>
    import { createForm } from "svelte-forms-lib";
    import { navigate } from "svelte-routing";
    import { onMount } from 'svelte';
    import { createUser, getUser, updateUser } from "../../../services/userService";
    import GroupSearcher from "../../SearchBars/GroupSearcher.svelte";
    import { getGroup } from "../../../services/groupService";
    import FormCard from "../FormCard.svelte";
    import InputField from "../InputField.svelte";
    import * as yup from "yup";
    import RadioButtons from "../../SearchBars/RadioButtons.svelte";
    import toast from "svelte-french-toast";

    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('id');
    const genderOptions = [
        { value: "false", label: "Normale", id: "adminTypeNormal" },
        { value: "true", label: "Amministratore UGS", id: "adminTypeUGS" },
    ];
    let isEdit = !!userId;
    let firstPasswordFieldType = "password";
    let secondPasswordFieldType = "password";
    let isPressed = false;

    const { form, handleChange, handleSubmit, isValid, errors } = createForm({
        initialValues: {
            firstname: "",
            lastname: "",
            email: "",
            password: "",
            secondPassword: "",
            role: "ADMIN",
            group: "",
            ugsManager: "false"
        },
        validationSchema: yup.object().shape({
            firstname: yup.string().required("Inserisci un valore"),
            lastname: yup.string().required("Inserisci un valore"),
            email: yup.string().email("Inserisci una mail corretta").required("Inserisci un valore"),
            password: yup.string().optional(),
            secondPassword: yup.string().optional(),
            role: yup.string().required("Inserisci un valore"),
            group: yup.mixed().optional(),
            ugsManager: yup.boolean().required("Inserisci un valore")
        }),
        onSubmit: (formUser) => {
            isPressed = true;

            if(formUser.role === "GROUP_MANAGER") {
                formUser.groupId = $form.group.value;
            }

            if(!isEdit && (!formUser.password || !formUser.secondPassword) || (formUser.password !== formUser.secondPassword)) {
                toast.error("Le due password non coincidono");
                isPressed = false;
                return;
            }

            delete formUser.group;
            delete formUser.secondPassword;

            if (isEdit) {
                editUser(formUser)
                return;
            }
            addUser(formUser);
        },
    });

    async function editUser (user) {
        delete user.password;
        updateUser(user, userId).then(() => {
            isPressed = false;
            toast.success("Utente modificato");
            navigate("table", { replace: true })
        }).catch((err) => {
            isPressed = false;
            if(err?.response?.status === 404) {
                toast.error("Utente non trovato");
                return;
            }
            if(err?.response?.status === 500) {
                toast.error("Questa mail è stata già modificata");
                return;
            }
            defaultError();
        })
    }
    async function addUser(user) {
        createUser(user).then(() => {
            isPressed = false;
            toast.success("Utente creato");
            navigate("table", {replace: true})
        }).catch((err) => {
            isPressed = false;
            if(err?.response?.status === 500) {
                toast.error("Questa mail è stata già utilizzata");
            }
        })
    }

    async function setGroup(groupId) {
        await getGroup(groupId).then((data) => {
            $form.group = {label: data.data.name, value: data.data.id};
        }).catch((err) => {
            if(err?.response?.status === 404) {
                toast.error(`Gruppo non trovato, errore ${err.toString()}`);
                return;
            }

            if(err?.response?.status === 500) {
                toast.error(`Errore nella ricerca del gruppo, errore ${err.toString()}`);
            }
        })
    }

    onMount(async () => {
        if(isEdit) {
            getUser(userId).then((data) => {
                if (data?.status === 200) {
                    const user = data.data;

                    form.set({
                        firstname: user.firstname,
                        lastname: user.lastname,
                        email: user.email,
                        role: user.role,
                        ugsManager: user.ugsManager.toString()
                    });

                    if(user.role === "GROUP_MANAGER" && user?.groupId) {
                         setGroup(user.groupId);
                    }
                }
            }).catch((err) => {
                if(err?.response?.data?.status === 404) {
                    toast.error("Utente non trovato");
                    navigate("table" , { replace: false })
                }
            });
        }
    });

    $: toggleFirstPasswordField = () => firstPasswordFieldType = firstPasswordFieldType ===  "text" ? "password" : "text";
    $: toggleSecondPasswordField = () => secondPasswordFieldType = secondPasswordFieldType === "text" ? "password" : "text";
</script>


<FormCard
    subject="l'utente"
    isEdit={isEdit}
    isButtonDisabled={isPressed || !$isValid}
    handleSubmit={handleSubmit}
    hasDeleteButton={false}
>
    <div class="w-full flex flex-col gap-3 md:flex-row">
        <InputField
            id="firstname"
            fieldName="Nome"
            placeholder="Nome dell'utente"
            name="firstname"
            bind:errorText={$errors.firstname}
            bind:value={$form.firstname}
            isRequired={false}
            handleChange={handleChange}
        />

        <InputField
            id="lastname"
            fieldName="Cognome"
            placeholder="Nome dell'utente"
            name="lastname"
            bind:errorText={$errors.lastname}
            bind:value={$form.lastname}
            isRequired={false}
            handleChange={handleChange}
        />
    </div>

    <InputField
        id="email"
        fieldName="Email"
        placeholder="Email dell'utente"
        min="{0}"
        name="email"
        bind:errorText={$errors.email}
        bind:value={$form.email}
        isRequired={false}
        handleChange={handleChange}
    />

    {#if !isEdit}
        <div class="w-full flex flex-col gap-3 md:flex-row">
            <div class="w-full md:w-1/2 relative">
                <InputField
                    id="firstPassword"
                    fieldName="Password"
                    placeholder="Password"
                    name="password"
                    bind:errorText={$errors.password}
                    bind:value={$form.password}
                    bind:type={firstPasswordFieldType}
                    isRequired={false}
                    handleChange={handleChange}
                />

                <i class="fa {firstPasswordFieldType === 'text' ? 'fa-eye' : 'fa-eye-slash'} absolute icon-position cursor-pointer" on:click|preventDefault={toggleFirstPasswordField}></i>
            </div>
            <div class="w-full md:w-1/2 relative">
                <InputField
                    id="secondPassword"
                    fieldName="Ripeti password"
                    placeholder="Ripeti password"
                    name="secondPassword"
                    bind:errorText={$errors.secondPassword}
                    bind:value={$form.secondPassword}
                    bind:type={secondPasswordFieldType}
                    isRequired={false}
                    handleChange={handleChange}
                />

                <i class="fa {secondPasswordFieldType ? 'fa-eye' : 'fa-eye-slash'} absolute icon-position cursor-pointer" on:click|preventDefault={toggleSecondPasswordField}></i>
            </div>
        </div>
    {/if}

    <div class="w-full flex flex-wrap">
        <div class="w-full">
            <div class="font-450 text-lg">Ruolo</div>
            <select
                class="w-full pr-4 h-11 input-home"
                name="role"
                bind:value={$form.role}
                on:blur={handleChange}
            >
                <option value="ADMIN" selected>Admin</option>
                <option value="GROUP_MANAGER">Amministratore di gruppo</option>
                <option value="SUBSCRIBER">Iscritto</option>
            </select>
        </div>
    </div>

    {#if $form.role === "GROUP_MANAGER"}
        <div class="w-full">
            <div class="font-450">Gruppo</div>

            <GroupSearcher
                bind:value={$form.group}
                on:change={handleChange}
                required={false}
            />
        </div>

        <div class="w-full">
            <div class="font-450">Tipo amministratore</div>

            <RadioButtons
                isNullable={false}
                options={genderOptions}
                bind:inputValue={$form.ugsManager}
            />
        </div>
    {/if}

</FormCard>

<style>
    .icon-position {
        top: 42px;
        right: 20px;
    }
</style>